// The floor's order of work (Mal kabul → Yerleştirme → Toplama → Paketleme → Sevkiyat, with İade kabul →
// Rafa iade, Sayım and the orders waiting for stock beside it): how much waits at each step, and which
// work comes next.
import { fmt } from './format';
import type { FloorSummary } from './types';
import type { IconName } from './ui/icons';

export type FlowStepKey = 'kabul' | 'yerlestirme' | 'toplama' | 'paketleme' | 'sevkiyat' | 'iade' | 'rafa-iade' | 'sayim' | 'stok-bekleyen';

/** A step of the floor's work as the Özet page lists it. */
export interface FlowStep {
  readonly key: FlowStepKey;
  readonly label: string;
  /** the screen where it is done */
  readonly path: string;
  readonly icon: IconName;
  /** how much waits there; 0: nothing */
  readonly waiting: number;
  /** "5 sipariş masada bekliyor", or what an empty step says */
  readonly text: string;
}

/** The work to do next, and the button that leads to it. */
export interface NextWork {
  readonly key: FlowStepKey;
  readonly path: string;
  readonly icon: IconName;
  readonly waiting: number;
  /** "5 sipariş paketlenmeyi bekliyor" */
  readonly what: string;
  /** "Paketlemeye geç" */
  readonly action: string;
  /** what the hands do there, in one sentence */
  readonly how: string;
}

const n = (value: number) => fmt.n(value);
const order = (s: FloorSummary, status: keyof FloorSummary['orders']) => s.orders[status] ?? 0;

/** Orders at each screen: picking counts the reserved ones and those on a route; the table counts the ones open at a desk too. */
const toPick = (s: FloorSummary) => order(s, 'allocated') + order(s, 'picking');
const toPack = (s: FloorSummary) => order(s, 'picked') + order(s, 'packing');
// on every screen through the tab bar: a summary without them (an older server) counts nothing, not a crash
const toCount = (s: FloorSummary) => s.counts?.open ?? 0;
const expected = (s: FloorSummary) => s.expectedReturns ?? 0;
const waitingStock = (s: FloorSummary) => order(s, 'backordered');

function step(key: FlowStepKey, s: FloorSummary): FlowStep {
  switch (key) {
    case 'kabul':
      return { key, label: 'Mal kabul', path: '/kabul', icon: 'inbox', waiting: s.openInbound, text: s.openInbound > 0 ? `${n(s.openInbound)} gönderi kabul bekliyor` : 'Bekleyen gönderi yok' };
    case 'yerlestirme':
      return {
        key,
        label: 'Yerleştirme',
        path: '/yerlestirme',
        icon: 'shelf',
        waiting: s.putawayTasks,
        text: s.putawayTasks > 0 ? `${n(s.putawayTasks)} ürün rafa konmayı bekliyor` : 'Rafa konacak ürün yok',
      };
    case 'toplama': {
      // the count is orders: the reserved ones and those on a route being walked, as the text says
      const allocated = order(s, 'allocated');
      const picking = order(s, 'picking');
      const parts = [
        allocated > 0 ? `${n(allocated)} sipariş toplanmayı bekliyor` : '',
        picking > 0 ? `${n(picking)} sipariş açık rotada` : s.openRoutes > 0 ? `${n(s.openRoutes)} rota açık` : '',
      ].filter(Boolean);
      return { key, label: 'Toplama', path: '/toplama', icon: 'route', waiting: toPick(s), text: parts.length > 0 ? parts.join(' · ') : 'Toplanacak sipariş yok' };
    }
    case 'paketleme':
      return { key, label: 'Paketleme', path: '/paketleme', icon: 'box', waiting: toPack(s), text: toPack(s) > 0 ? `${n(toPack(s))} sipariş masada bekliyor` : 'Masada sipariş yok' };
    case 'sevkiyat': {
      const packed = order(s, 'packed');
      const today = s.shippedToday > 0 ? ` · bugün ${n(s.shippedToday)} paket verildi` : '';
      return { key, label: 'Sevkiyat', path: '/sevkiyat', icon: 'truck', waiting: packed, text: `${packed > 0 ? `${n(packed)} paket kurye bekliyor` : 'Kargoya verilecek paket yok'}${today}` };
    }
    case 'iade':
      return {
        key,
        label: 'İade kabul',
        path: '/iade',
        icon: 'undo',
        waiting: expected(s),
        text: expected(s) > 0 ? `${n(expected(s))} iade bekleniyor` : 'Beklenen iade yok',
      };
    case 'rafa-iade':
      return {
        key,
        label: 'Rafa iade',
        path: '/yerlestirme',
        icon: 'shelf',
        waiting: s.returnTasks,
        text: s.returnTasks > 0 ? `${n(s.returnTasks)} iade ürünü rafa dönmeyi bekliyor` : 'Rafa dönecek ürün yok',
      };
    case 'sayim':
      return { key, label: 'Sayım', path: '/sayim', icon: 'clipboard', waiting: toCount(s), text: toCount(s) > 0 ? `${n(toCount(s))} göz sayılmayı bekliyor` : 'Sayılacak göz yok' };
    case 'stok-bekleyen':
      return {
        key,
        label: 'Stok bekleyen',
        path: '/stok-bekleyen',
        icon: 'clock',
        waiting: waitingStock(s),
        text: waitingStock(s) > 0 ? `${n(waitingStock(s))} sipariş stok bekliyor` : 'Stok bekleyen sipariş yok',
      };
  }
}

/** The five steps every order and every delivery goes through, in the floor's order. */
export const FLOW: readonly FlowStepKey[] = ['kabul', 'yerlestirme', 'toplama', 'paketleme', 'sevkiyat'];
/** Beside them: returns back to the shelf, bins to count, and orders waiting for goods the warehouse may hold unentered. */
export const SIDE_FLOW: readonly FlowStepKey[] = ['iade', 'rafa-iade', 'sayim', 'stok-bekleyen'];

export const floorFlow = (s: FloorSummary, keys: readonly FlowStepKey[] = FLOW): FlowStep[] => keys.map((key) => step(key, s));

/**
 * The rule for "Sıradaki iş": finish what is closest to leaving first. Orders on the table before
 * orders on the shelf, goods to put away before the next delivery is opened; a bin locked for
 * counting holds orders back, so it comes before a new delivery. The handover is last: parcels
 * wait for the courier, whenever he comes, and the floor goes on with the rest meanwhile (the Özet
 * page still shows them, and the Sevkiyat tab has its count). Orders waiting for stock come once the
 * dock is done, before the parcels: the goods may be in the warehouse without having been entered (a
 * pallet that came with no shipment), and nobody else looks for them. İade kabul is not here: returns
 * are expected, not at the desk, until the parcel comes.
 */
export const NEXT_ORDER: readonly FlowStepKey[] = ['paketleme', 'toplama', 'yerlestirme', 'rafa-iade', 'sayim', 'kabul', 'stok-bekleyen', 'sevkiyat'];

function advice(key: FlowStepKey, s: FloorSummary): Omit<NextWork, 'key' | 'path' | 'icon' | 'waiting'> {
  switch (key) {
    case 'paketleme':
      return { what: `${n(toPack(s))} sipariş paketlenmeyi bekliyor`, action: 'Paketlemeye geç', how: 'Sepetteki siparişi okutun, ürünleri okutarak koliye koyun.' };
    case 'toplama': {
      const allocated = order(s, 'allocated');
      return allocated > 0
        ? { what: `${n(allocated)} sipariş toplanmayı bekliyor`, action: 'Toplamaya geç', how: 'Siparişleri seçip rota oluşturun ya da açık rotayı sürdürün.' }
        : {
            what: s.openRoutes > 0 ? `${n(s.openRoutes)} toplama rotası bitmedi` : `${n(toPick(s))} sipariş toplanıyor`,
            action: 'Toplamaya geç',
            how: 'Açık rotayı açıp kalan durakları toplayın.',
          };
    }
    case 'yerlestirme':
      return { what: `${n(s.putawayTasks)} ürün rafa konmayı bekliyor`, action: 'Yerleştirmeye geç', how: 'Kabul alanındaki ürünleri önerilen göze koyup gözü okutun.' };
    case 'rafa-iade':
      return { what: `${n(s.returnTasks)} iade ürünü rafa dönmeyi bekliyor`, action: 'Rafa iadeye geç', how: 'İade alanındaki sağlam ürünleri raflarına koyup gözü okutun.' };
    case 'sayim':
      return { what: `${n(toCount(s))} göz sayılmayı bekliyor`, action: 'Sayıma geç', how: 'Kilitli gözdeki ürünleri sayın; sayım tutunca göz açılır.' };
    case 'kabul':
      return { what: `${n(s.openInbound)} gönderi kabul bekliyor`, action: 'Mal kabule geç', how: 'Gelen kolinin gönderi numarasını okutup ürünleri sayın.' };
    case 'stok-bekleyen':
      return {
        what: `${n(waitingStock(s))} sipariş stok bekliyor`,
        action: 'Stok bekleyenlere bak',
        how: 'Ürün depodaysa mal kabulden stoğa girin; değilse satıcının gönderisi bekleniyor.',
      };
    case 'sevkiyat':
      // said with the courier: a parcel read at the dock counts as handed over
      return { what: `${n(order(s, 'packed'))} paket kurye gelince kargoya verilecek`, action: 'Sevkiyata geç', how: 'Kurye gelince her paketin kargo etiketini okutun.' };
    default:
      return { what: '', action: '', how: '' };
  }
}

/**
 * The one work to do next (NEXT_ORDER), or null when nothing waits. `except`: the screen the
 * worker is on and has just emptied — its count may still be a few seconds old.
 */
export function nextWork(s: FloorSummary, except?: string): NextWork | null {
  for (const key of NEXT_ORDER) {
    const at = step(key, s);
    if (at.waiting <= 0 || at.path === except) continue;
    return { key, path: at.path, icon: at.icon, waiting: at.waiting, ...advice(key, s) };
  }
  return null;
}

/** How much work waits at a screen (the quiet count on a phone's tab); 0 for any other screen. */
export function waitingAt(s: FloorSummary, path: string): number {
  return floorFlow(s, [...FLOW, ...SIDE_FLOW])
    .filter((at) => at.path === path && at.key !== 'iade')
    .reduce((sum, at) => sum + at.waiting, 0);
}
